import { useState } from 'react';

import { format } from 'date-fns';
import { DateRange } from 'react-day-picker';

import { useDownloadCreditUsage } from '@/api/mutations/useDownloadCreditUsage';
import { useGetLedgerBillingOverview } from '@/api/queries/useGetLedgerBillingOverview';
import { Icon } from '@/new/components/icon';
import { SettingsSectionHeading } from '@/new/components/settings-section';
import { Button } from '@/new/components/shadcn/button';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/new/components/shadcn/empty';
import { cn } from '@/new/lib/utils';
import { UsageHistoryChart } from '@/new/pages/components/usage-history-chart';
import { UsageHistoryTable } from '@/new/pages/components/usage-history-table';

function parseCalendarDate(value: string): Date {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
}

const longDateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});
export function BillingUsage({
  organisationId,
  isViewingOtherOrg = false,
}: {
  organisationId: string;
  isViewingOtherOrg?: boolean;
}) {
  const [dateSelection, setDateSelection] = useState<{ organisationId: string; range: DateRange }>();
  const dateRange = dateSelection?.organisationId === organisationId ? dateSelection.range : undefined;
  const completeDateRange = dateRange?.from && dateRange.to ? dateRange : undefined;
  const { data, isLoading } = useGetLedgerBillingOverview(organisationId, {
    startDate: completeDateRange ? format(completeDateRange.from!, 'yyyy-MM-dd') : undefined,
    endDate: completeDateRange ? format(completeDateRange.to!, 'yyyy-MM-dd') : undefined,
  });
  const { fetchDownload, isLoading: isDownloading } = useDownloadCreditUsage();

  if (isLoading) {
    return (
      <div className="flex h-full animate-pulse items-center justify-center gap-2 text-sm text-gray-500">
        <Icon className="lucide--loader animate-spin ease-in-out" />
        <span>Loading credit usage data...</span>
      </div>
    );
  }

  if (!data) {
    return (
      <Empty className="h-full">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icon className="lucide--triangle-alert" />
          </EmptyMedia>
          <EmptyTitle>Usage unavailable</EmptyTitle>
          <EmptyDescription>Credit usage could not be loaded.</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  const billingPeriod = data.billingPeriod;
  const isPersonalUsage = data.isPersonalUsage;
  const percentage = billingPeriod?.maxCredits
    ? Math.min((billingPeriod.usedCredits / billingPeriod.maxCredits) * 100, 100)
    : null;
  const selectedDateRange =
    completeDateRange ??
    ({
      from: data.startDate
        ? parseCalendarDate(data.startDate)
        : data.timeSeries[0]?.date
          ? parseCalendarDate(data.timeSeries[0].date)
          : new Date(),
      to: data.endDate ? parseCalendarDate(data.endDate) : new Date(),
    } satisfies DateRange);
  const downloadStartDate = selectedDateRange.from;
  const downloadEndDate = selectedDateRange.to;

  return (
    <div className="mb-32 space-y-6">
      <SettingsSectionHeading
        title="Usage"
        description={
          isPersonalUsage
            ? 'Review your credit usage for the selected date range.'
            : isViewingOtherOrg
              ? "Review this organisation's credit usage for the selected date range."
              : "Review your organisation's credit usage for the selected date range."
        }
        inset={false}
        action={
          <Button
            disabled={isDownloading || !downloadStartDate || !downloadEndDate}
            onClick={() => {
              if (!downloadStartDate || !downloadEndDate) return;
              fetchDownload({ organisationId, startDate: downloadStartDate, endDate: downloadEndDate });
            }}
            className="mt-2"
          >
            <Icon className={isDownloading ? 'lucide--loader animate-spin ease-in-out' : 'lucide--download'} />
            Download credit usage
          </Button>
        }
      />

      <div className={cn(billingPeriod && '-mx-2 rounded-2xl bg-gray-100/70')}>
        {billingPeriod && (
          <section className="rounded-xl px-6 py-4.5 pb-5.5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <p className="text-sm font-medium text-gray-600">
                {isPersonalUsage
                  ? 'Your credit usage for the current billing period'
                  : 'Credit usage for current billing period'}
              </p>
              <p className="text-sm">
                <span className="text-gray-600">
                  {billingPeriod.startDate ? longDateFormatter.format(parseCalendarDate(billingPeriod.startDate)) : '—'}
                </span>
                <span className="mx-1.5 text-gray-300">–</span>
                <span className="text-gray-600">
                  {billingPeriod.endDate ? longDateFormatter.format(parseCalendarDate(billingPeriod.endDate)) : '—'}
                </span>
              </p>
            </div>
            <p className="mt-3 px-0.5 text-3xl font-semibold text-gray-800 tabular-nums">
              {billingPeriod.usedCredits.toLocaleString('en-GB', { maximumFractionDigits: 1 })}
              {billingPeriod.maxCredits !== null && (
                <span className="ml-2 text-base font-normal text-gray-400">
                  / {billingPeriod.maxCredits.toLocaleString('en-GB')} credits
                </span>
              )}
            </p>

            {billingPeriod.maxCredits !== null && percentage !== null && (
              <div className="mt-4">
                <div className="mb-2 flex justify-between px-1 text-gray-500">
                  <span>{percentage.toFixed(1)}% used</span>
                  <span>
                    {Math.max(billingPeriod.maxCredits - billingPeriod.usedCredits, 0).toLocaleString('en-GB')}{' '}
                    remaining
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-gray-200">
                  <div className="h-full rounded-full bg-teal-400" style={{ width: `${percentage}%` }} />
                </div>
              </div>
            )}
          </section>
        )}

        <UsageHistoryChart
          timeSeries={data.timeSeries ?? []}
          dateRange={selectedDateRange}
          onDateRangeChange={(range) => setDateSelection({ organisationId, range })}
        />
      </div>

      <UsageHistoryTable rows={data.tableRows ?? []} isPersonalUsage={isPersonalUsage} />
    </div>
  );
}
